import type { AnswerModel } from '@shared/models/Answer';
import { Box, Chip, Typography } from '@mui/material';
import moment from 'moment';
import React from 'react';
import styled from 'styled-components';
import AuthorBadge from '@shared/components/AuthorBadge';

interface AnswerCardProps {
  answer: AnswerModel;
}

const AnswerContainer = styled(Box)`
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.04) 0%, rgba(56, 142, 60, 0.02) 100%);
  border-left: 3px solid #4CAF50;
  border-radius: 12px;
  padding: 20px;
  position: relative;
`;

const AnswerHeader = styled(Box)`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

const AnswerBadge = styled(Chip)`
  background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%);
  color: white;
  font-weight: 600;
  font-size: 0.75rem;
  height: 24px;
  box-shadow: 0 2px 8px rgba(76, 175, 80, 0.2);

  & .MuiChip-label {
    padding: 0 10px;
  }
`;

const AnswerText = styled(Typography)`
  font-size: 0.9375rem;
  line-height: 1.7;
  color: rgba(0, 0, 0, 0.8);
  white-space: pre-wrap;
  margin-bottom: 12px;
`;

const AnswerMeta = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 16px;
  flex-wrap: wrap;
  gap: 12px;
`;

const Timestamp = styled(Typography)`
  font-size: 0.8125rem;
  color: rgba(0, 0, 0, 0.5);
`;

const AnswerCard: React.FC<AnswerCardProps> = ({ answer }) => {
  const formatDate = (date: string | Date) => {
    return moment(date).fromNow();
  };

  return (
    <AnswerContainer>
      <AnswerHeader>
        <AnswerBadge label="✓ Answered" />
      </AnswerHeader>
      <AnswerText>{answer.answer}</AnswerText>
      <AnswerMeta>
        {answer.user && (
          <AuthorBadge
            userId={answer.userId}
            user={answer.user}
            size="small"
          />
        )}
        <Timestamp>{formatDate(answer.createdAt)}</Timestamp>
      </AnswerMeta>
    </AnswerContainer>
  );
};

export default AnswerCard;
